import React from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Svg, { Path, Rect } from 'react-native-svg';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { typography } from '../../../shared/theme/typography';
import {
  formatArs,
  formatGrantExpiryLabel,
  planCatalogItem,
  planDisplayName,
  type PublicBillingCatalog,
} from '../../../../../../shared/billing-catalog';
import type { PlanEntitlementsLike } from '../../../../../../shared/plan-benefits';
import type { PaidPlanCode } from '../../feature-flags/planIntentService';
import { hottestStructuralUsage, type UsageDetail } from '../softLimitUsage';

type VolumeMetric = { key: string; used: number; limit: number; ratio: number };

type EntitlementsWithVolume = PlanEntitlementsLike & {
  volume_soft_limit?: { metrics?: VolumeMetric[] } | null;
};

const DELTA_FIELDS: Array<{ key: string; label: string; monthly: boolean }> = [
  { key: 'monthly_pos_transactions_limit', label: 'Ventas', monthly: true },
  { key: 'monthly_online_orders_limit', label: 'Pedidos online', monthly: true },
  { key: 'monthly_appointments_limit', label: 'Turnos', monthly: true },
  { key: 'max_branches', label: 'Sucursales', monthly: false },
  { key: 'max_users', label: 'Usuarios', monthly: false },
];

const METRIC_FIELD: Record<string, string> = {
  pos_transactions: 'monthly_pos_transactions_limit',
  online_orders: 'monthly_online_orders_limit',
  appointments: 'monthly_appointments_limit',
};

const CRECE_EXTRA_LINES = ['Equipo, auditoría y análisis avanzado'];

function fmt(n: number): string {
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(n);
}

/** Líneas "hoy X → Y" comparando el plan actual contra el objetivo. */
export function planDeltaLines(
  catalog: PublicBillingCatalog,
  currentPlanCode: string,
  targetPlanCode: string,
): string[] {
  const current = planCatalogItem(catalog, currentPlanCode);
  const target = planCatalogItem(catalog, targetPlanCode);
  if (!target) return [];
  const lines: string[] = [];

  for (const field of DELTA_FIELDS) {
    const cur = current ? (current as Record<string, unknown>)[field.key] : null;
    const tgt = (target as Record<string, unknown>)[field.key];
    const curNum = typeof cur === 'number' ? cur : null;
    const tgtNum = typeof tgt === 'number' ? tgt : null;
    const suffix = field.monthly ? '/mes' : '';

    if (tgtNum == null && (curNum == null || curNum > 0)) {
      lines.push(
        curNum != null
          ? `↑ ${field.label} sin tope · hoy ${fmt(curNum)}`
          : `↑ ${field.label} sin tope`,
      );
    } else if (tgtNum != null && (curNum == null || tgtNum > curNum)) {
      lines.push(
        curNum != null
          ? `↑ ${field.label} hasta ${fmt(tgtNum)}${suffix} · hoy ${fmt(curNum)}`
          : `↑ ${field.label} hasta ${fmt(tgtNum)}${suffix}`,
      );
    }
  }

  if (targetPlanCode === 'crece') lines.push(...CRECE_EXTRA_LINES);
  return lines.slice(0, 4);
}

/** Métrica de volumen más caliente si supera el 80% del tope. */
function hotMetric(metrics: VolumeMetric[] | undefined): VolumeMetric | null {
  if (!metrics?.length) return null;
  const hottest = [...metrics].sort((a, b) => b.ratio - a.ratio)[0];
  return hottest && hottest.ratio >= 0.8 ? hottest : null;
}

/** Plan visible más barato que levanta el tope de la métrica caliente. */
function recommendedPlan(
  catalog: PublicBillingCatalog,
  plans: PaidPlanCode[],
  metric: VolumeMetric | null,
): PaidPlanCode | null {
  const field = metric ? METRIC_FIELD[metric.key] : null;
  if (!field || !metric) return null;
  for (const plan of plans) {
    const item = planCatalogItem(catalog, plan);
    const limit = item ? (item as Record<string, unknown>)[field] : null;
    if (limit == null || (typeof limit === 'number' && limit > metric.limit)) return plan;
  }
  return null;
}

/** Opciones de upgrade según el plan actual (compartido con el prefetch). */
export function planOptionsFor(currentPlanCode: string): PaidPlanCode[] {
  return currentPlanCode === 'legacy' || currentPlanCode === 'emprende'
    ? ['crece']
    : ['emprende', 'crece'];
}

/** Tope estructural ya completo (sucursales/equipo): solo Crece lo levanta. */
function structuralAtLimit(
  entitlements: EntitlementsWithVolume | null | undefined,
): UsageDetail | null {
  const detail = entitlements
    ? hottestStructuralUsage({
        branch_count: entitlements.branch_count,
        max_branches: entitlements.max_branches ?? null,
        user_count: entitlements.user_count,
        max_users: entitlements.max_users ?? null,
      })
    : null;
  return detail != null && detail.remaining === 0 ? detail : null;
}

/** Plan recomendado por uso real: volumen caliente primero; si no hay señal de
 * volumen pero el tope estructural está completo, solo Crece lo levanta. */
export function recommendedUpgradePlan(
  catalog: PublicBillingCatalog,
  entitlements: EntitlementsWithVolume | null | undefined,
  currentPlanCode: string,
): PaidPlanCode | null {
  const plans = planOptionsFor(currentPlanCode);
  const byVolume = recommendedPlan(catalog, plans, hotMetric(entitlements?.volume_soft_limit?.metrics));
  if (byVolume) return byVolume;
  return structuralAtLimit(entitlements) != null && plans.includes('crece') ? 'crece' : null;
}

export function PlanOptionsPreview({
  catalog,
  entitlements,
  currentPlanCode,
  onSelect,
  onPrefetch,
  busyPlan,
  ctaLabel,
  trustLine,
}: {
  catalog: PublicBillingCatalog;
  entitlements: EntitlementsWithVolume | null | undefined;
  /** Plan efectivo actual: 'gratis' | 'emprende' | 'legacy' (completo/esencial → legacy). */
  currentPlanCode: string;
  onSelect: (plan: PaidPlanCode) => void;
  /** Precarga del checkout al presionar la card (web: acorta el redirect a MP). */
  onPrefetch?: (plan: PaidPlanCode) => void;
  /** Plan con checkout en curso: muestra spinner y deshabilita ambas cards. */
  busyPlan?: PaidPlanCode | null;
  /** Label del CTA de cada card (default "Elegir →"). */
  ctaLabel?: string;
  /** Línea de confianza bajo las tarjetas (canal de cobro según superficie). */
  trustLine?: string;
}) {
  const plans = planOptionsFor(currentPlanCode);

  const metric = hotMetric(entitlements?.volume_soft_limit?.metrics);
  const structural = structuralAtLimit(entitlements);
  const volumeReco = recommendedPlan(catalog, plans, metric);
  const recommended = volumeReco ?? (structural && plans.includes('crece') ? 'crece' : null);

  let recoBanner: string | null = null;
  if (metric && volumeReco) {
    const item = planCatalogItem(catalog, volumeReco);
    const limit = item ? (item as Record<string, unknown>)[METRIC_FIELD[metric.key]] : null;
    const next = typeof limit === 'number' ? `${fmt(limit)} al mes` : 'uso sin tope';
    recoBanner = `Este mes ya usaste ${fmt(metric.used)} de ${fmt(metric.limit)}. ${planDisplayName(volumeReco)} te da ${next}.`;
  } else if (structural && recommended === 'crece') {
    recoBanner = `Tenés ${structural.meta}. Solo Crece suma más.`;
  }

  return (
    <View style={styles.block}>
      <View style={styles.header}>
        <Text style={styles.eyebrow}>Elegí tu plan</Text>
        <Text style={styles.title}>Cuando tu negocio avance</Text>
      </View>

      {recoBanner ? (
        <View style={styles.recoBanner}>
          <Text style={styles.recoBannerText}>{recoBanner}</Text>
        </View>
      ) : null}

      <View style={styles.cards}>
        {plans.map((plan) => {
          const item = planCatalogItem(catalog, plan);
          const price = item?.price_ars_monthly ?? 0;
          const daily = price > 0 ? Math.round(price / 30) : 0;
          const lines = planDeltaLines(catalog, currentPlanCode === 'legacy' ? 'crece' : currentPlanCode, plan);
          const isRecommended = recommended === plan;
          const ribbon = isRecommended
            ? 'Te conviene'
            : plan === 'crece'
              ? 'Todo incluido'
              : null;

          const upgradeNote =
            plan === 'crece' && currentPlanCode === 'emprende'
              ? `Crece queda activo al confirmar. El próximo cobro llega cuando termine tu período de Emprende${
                  entitlements?.subscription_expires_at
                    ? `, el ${formatGrantExpiryLabel(entitlements.subscription_expires_at)}`
                    : ''
                }.`
              : null;

          return (
            <TouchableOpacity
              key={plan}
              accessibilityRole="button"
              activeOpacity={0.85}
              disabled={busyPlan != null}
              onPressIn={onPrefetch ? () => onPrefetch(plan) : undefined}
              onPress={() => onSelect(plan)}
              style={[styles.card, plan === 'crece' && styles.cardCrece]}
            >
              <View style={styles.cardHead}>
                <View style={styles.nameRow}>
                  <Text style={styles.planName}>{planDisplayName(plan)}</Text>
                  {plan === 'emprende' ? (
                    <View style={styles.badgeNew}>
                      <Text style={styles.badgeNewText}>Nuevo</Text>
                    </View>
                  ) : null}
                </View>
                {ribbon ? (
                  <View style={[styles.ribbon, isRecommended && styles.ribbonReco]}>
                    <Text style={styles.ribbonText}>{ribbon}</Text>
                  </View>
                ) : null}
              </View>

              <View style={styles.priceRow}>
                <Text style={styles.price}>{formatArs(price)}</Text>
                <Text style={styles.pricePeriod}>/ mes</Text>
              </View>
              {daily > 0 ? <Text style={styles.daily}>≈ {formatArs(daily)} por día</Text> : null}

              <View style={styles.deltaList}>
                {lines.map((line) => (
                  <Text key={line} style={styles.deltaLine}>
                    {line}
                  </Text>
                ))}
              </View>

              {upgradeNote ? <Text style={styles.upgradeNote}>{upgradeNote}</Text> : null}

              <View style={[styles.ctaVisual, plan === 'crece' ? styles.ctaCrece : styles.ctaSoft]}>
                {busyPlan === plan ? (
                  <ActivityIndicator
                    size="small"
                    color={plan === 'crece' ? ticketColors.creceInk : ticketColors.brandInk}
                  />
                ) : (
                  <Text style={plan === 'crece' ? styles.ctaCreceText : styles.ctaSoftText}>
                    {ctaLabel ?? 'Elegir →'}
                  </Text>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>

      {trustLine ? (
        <View style={styles.trustRow}>
          <Svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={ticketColors.ink3} strokeWidth={2.4} strokeLinecap="round">
            <Rect x={4} y={10} width={16} height={11} rx={2} />
            <Path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </Svg>
          <Text style={styles.trust}>{trustLine}</Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  block: { gap: spacing.md, width: '100%', alignSelf: 'stretch' },
  header: { gap: 2 },
  eyebrow: {
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
  },
  title: {
    ...typography.titleLg,
    fontFamily: ticketFonts.sans,
    fontSize: 20,
    fontWeight: '800',
    color: ticketColors.ink,
    letterSpacing: -0.5,
  },
  recoBanner: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  recoBannerText: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    lineHeight: 19,
    color: ticketColors.ink2,
  },
  cards: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    width: '100%',
  },
  card: {
    flexGrow: 1,
    flexBasis: 240,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
    padding: spacing.md,
    gap: spacing.sm,
  },
  cardCrece: {
    borderColor: ticketColors.crece,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexShrink: 1 },
  planName: {
    fontFamily: ticketFonts.sans,
    fontSize: 17,
    fontWeight: '800',
    color: ticketColors.ink,
  },
  badgeNew: {
    borderRadius: 6,
    backgroundColor: 'rgba(74, 222, 128, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(74, 222, 128, 0.45)',
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  badgeNewText: {
    fontFamily: ticketFonts.sans,
    fontSize: 10,
    fontWeight: '800',
    color: ticketColors.ok,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  ribbon: {
    borderRadius: 6,
    backgroundColor: ticketColors.crece,
    paddingHorizontal: 6,
    paddingVertical: 3,
    flexShrink: 0,
  },
  ribbonReco: {
    backgroundColor: ticketColors.gold,
  },
  ribbonText: {
    fontFamily: ticketFonts.sans,
    fontSize: 10,
    fontWeight: '800',
    color: ticketColors.creceInk,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  priceRow: { flexDirection: 'row', alignItems: 'baseline', gap: spacing.xs },
  price: {
    fontFamily: ticketFonts.mono,
    fontSize: 24,
    fontWeight: '700',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
    letterSpacing: -0.3,
  },
  pricePeriod: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
  },
  daily: {
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    color: ticketColors.ink3,
    marginTop: -spacing.xs,
  },
  upgradeNote: {
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    lineHeight: 16,
    color: ticketColors.ink3,
  },
  deltaList: { gap: 4 },
  deltaLine: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    lineHeight: 18,
    color: ticketColors.ink2,
  },
  ctaVisual: {
    marginTop: 'auto',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 9,
    minHeight: 40,
  },
  ctaCrece: { backgroundColor: ticketColors.crece },
  ctaSoft: {
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 1,
    borderColor: ticketColors.brandBorder,
  },
  ctaCreceText: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.creceInk,
  },
  ctaSoftText: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.brandInk,
  },
  trustRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  trust: {
    ...typography.caption,
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
    lineHeight: 18,
    textAlign: 'center',
  },
});
